import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTrackingPreference, TrackingCategory } from '@coinbase/cookie-manager';

type PersistedState<T> = [T, Dispatch<SetStateAction<T>>];

function usePersistedState<T>(defaultValue: T, key: string): PersistedState<T> {
  const { consent } = useTrackingPreference();
  const isFunctionalConsentAllowed = consent.includes(TrackingCategory.FUNCTIONAL);

  const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

  const [value, setValue] = useState<T>(() => {
    if (!isBrowser) {
      return defaultValue; // Fallback for non-browser environments
    }

    try {
      const storedValue = isFunctionalConsentAllowed && localStorage.getItem(key);
      return storedValue ? (JSON.parse(storedValue) as T) : defaultValue;
    } catch (error) {
      console.error(`Error accessing localStorage for key "${key}":`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (isBrowser && isFunctionalConsentAllowed) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error(`Error setting localStorage for key "${key}":`, error);
      }
    }
  }, [key, value, isBrowser, isFunctionalConsentAllowed]);

  return [value, setValue];
}

export { usePersistedState };
